import { LEGACY_STORAGE_KEY, STORAGE_KEY, loadProgress, migrateProgress } from "./progress.mjs";
import { mergeLanguage } from "./merge";

/**
 * Keeps the per-language progress in browser storage in step with the server (Postgres, per signed-in user).
 * Local storage stays the working copy. Changes are pushed shortly after they happen; the server is polled while
 * the app is open. A push names the server version it builds on; if another device has moved on, the two copies are
 * merged (see merge.ts) rather than one overwriting the other, and the merged result is pushed.
 */
export const META_KEY = "hibiki-sync-v1";
export const BASE_KEY = "hibiki-sync-base-v1";

export interface RemoteRow { data: unknown; updatedAt: number }
export interface SyncApi {
  /** Change clock per language; cheap enough to poll. */
  clocks(): Promise<Record<string, number>>;
  get(languageId: string): Promise<RemoteRow | null>;
  /**
   * Stores `data` if the server still holds the version `baseAt` (0: none). Otherwise nothing is written and the
   * server's current row comes back with `ok: false`, for the caller to merge.
   */
  put(languageId: string, data: unknown, baseAt: number, opts?: { keepalive?: boolean }): Promise<{ ok: boolean; row: RemoteRow }>;
}
interface LanguageMeta {
  /** Client clock of the last local change. */
  updatedAt: number;
  /** `updatedAt` as of the last successful push or adoption; `updatedAt > syncedAt` means unsynced changes. */
  syncedAt: number;
  /** The server's clock for this language as of the last time we saw it. */
  serverAt: number;
}
interface Meta { userId: string; languages: Record<string, LanguageMeta> }
type State = { version: 2; selectedLanguageId: string | null; languages: Record<string, any> };
type Json = Record<string, any>;

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));
function readJson<T>(storage: Storage, key: string): T | null {
  try { return JSON.parse(storage.getItem(key) ?? "null"); } catch { return null; }
}
function readMeta(storage: Storage): Meta | null {
  const meta = readJson<Meta>(storage, META_KEY);
  return meta && typeof meta.userId === "string" && meta.languages && typeof meta.languages === "object" ? meta : null;
}

export function createApi(getToken: () => Promise<string | null>, base = ""): SyncApi {
  async function call(path: string, init: { method?: string; body?: string; keepalive?: boolean; timeoutMs?: number } = {}): Promise<Response> {
    const token = await getToken();
    if (!token) throw new Error("not signed in");
    const res = await fetch(base + path, {
      method: init.method,
      body: init.body,
      keepalive: init.keepalive,
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      signal: AbortSignal.timeout(init.timeoutMs ?? 10_000),
    } as RequestInit); // `keepalive` is missing from the Workers RequestInit type that leaks into this project
    if (!res.ok && res.status !== 404 && res.status !== 409) throw new Error(`${init.method ?? "GET"} ${path}: ${res.status}`);
    return res;
  }
  const lang = (id: string) => `/api/progress/${encodeURIComponent(id)}`;
  return {
    clocks: async () => ((await (await call("/api/progress?clocks=1", { timeoutMs: 6_000 })).json()) as { clocks: Record<string, number> }).clocks,
    get: async (id) => { const res = await call(lang(id)); return res.status === 404 ? null : ((await res.json()) as RemoteRow); },
    put: async (id, data, baseAt, opts) => {
      const res = await call(lang(id), { method: "PUT", body: JSON.stringify({ data, baseAt }), keepalive: opts?.keepalive });
      return { ok: res.ok, row: (await res.json()) as RemoteRow };
    },
  };
}

/** Forget all local progress and sync bookkeeping (used on sign-out so the next person starts clean). */
export function clearLocal(storage: Storage) {
  for (const key of [STORAGE_KEY, LEGACY_STORAGE_KEY, META_KEY, BASE_KEY]) storage.removeItem(key);
}

/** Progress left on this device by a different user is discarded; anything else is kept for the new session. */
export function claimDevice(storage: Storage, userId: string) {
  const meta = readMeta(storage);
  if (meta && meta.userId !== userId) clearLocal(storage);
}

const RETRY_MS = [5_000, 15_000, 60_000];
const MAX_MERGE_ROUNDS = 3;

export interface SyncOptions {
  debounceMs?: number;
  /** Called when the server could (true) or could not (false) be reached. */
  onOnline?: (online: boolean) => void;
}

/** Watches the app's progress state, pushes changed languages, and merges in changes made elsewhere. */
export class ProgressSync {
  private meta: Meta;
  private base: Record<string, Json>;
  private snapshot = new Map<string, string>();
  private latest: State;
  private applyToApp: ((languageId: string, data: Json) => void) | null = null;
  private applying = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private queue: Promise<unknown> = Promise.resolve();
  private failures = 0;
  private opts: Required<Omit<SyncOptions, "onOnline">> & Pick<SyncOptions, "onOnline">;

  constructor(private storage: Storage, private api: SyncApi, userId: string, opts: SyncOptions = {}) {
    this.opts = { debounceMs: 2_000, ...opts };
    this.meta = readMeta(storage) ?? { userId, languages: {} };
    this.base = readJson<Record<string, Json>>(storage, BASE_KEY) ?? {};
    this.latest = loadProgress(storage);
    for (const [id, progress] of Object.entries(this.latest.languages)) {
      this.snapshot.set(id, JSON.stringify(progress));
      // Progress that predates sync is unsynced; its last activity is the best guess for when it changed.
      this.meta.languages[id] ??= { updatedAt: Number(progress.lastActivity?.at) || 1, syncedAt: 0, serverAt: 0 };
    }
    this.persistMeta();
    if (this.dirty().length) this.schedule(0);
  }

  /** Once the app's store exists, remote changes are applied to it (in place) instead of to storage. */
  attach(state: State, apply: (languageId: string, data: Json) => void) { this.latest = state; this.applyToApp = apply; }

  /** Call after every local save. Cheap when nothing changed. */
  observe(state: State) {
    this.latest = state;
    let changed = false;
    for (const [id, progress] of Object.entries(state.languages)) {
      const json = JSON.stringify(progress);
      if (this.snapshot.get(id) === json) continue;
      this.snapshot.set(id, json);
      if (this.applying) continue; // a remote change being applied is not a local edit
      const prev = this.meta.languages[id];
      this.meta.languages[id] = { updatedAt: Math.max(Date.now(), (prev?.updatedAt ?? 0) + 1), syncedAt: prev?.syncedAt ?? 0, serverAt: prev?.serverAt ?? 0 };
      changed = true;
    }
    if (!changed) return;
    this.persistMeta();
    this.schedule(this.opts.debounceMs);
  }

  dirty(): string[] {
    return Object.entries(this.meta.languages).filter(([, m]) => m.updatedAt > m.syncedAt).map(([id]) => id);
  }

  /** Check the server for changes from other devices and merge them in. Resolves false if it could not be reached. */
  pull(): Promise<boolean> {
    return this.enqueue(async () => {
      const clocks = await this.api.clocks();
      for (const [id, clock] of Object.entries(clocks)) {
        if (clock <= (this.meta.languages[id]?.serverAt ?? 0)) continue;
        const row = await this.api.get(id);
        if (row) this.receive(id, row);
      }
      this.persistMeta();
      if (this.dirty().length) this.schedule(0);
    }).then(() => true, () => false).then((ok) => { this.opts.onOnline?.(ok); return ok; });
  }

  /** Push everything unsynced now. Never rejects; failures leave the language dirty and schedule a retry. */
  flush(opts: { keepalive?: boolean } = {}): Promise<void> {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    return this.enqueue(() => this.pushAll(opts)).then(() => undefined, () => undefined);
  }

  // ---- internals ------------------------------------------------------------------------------------------------

  private enqueue<T>(job: () => Promise<T>): Promise<T> {
    const run = this.queue.then(job, job);
    this.queue = run.catch(() => undefined);
    return run;
  }

  private schedule(ms: number) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.timer = null; void this.flush(); }, ms);
    (this.timer as { unref?: () => void }).unref?.(); // never keep a Node process (tests, tooling) alive
  }

  private persistMeta() {
    try {
      this.storage.setItem(META_KEY, JSON.stringify(this.meta));
      this.storage.setItem(BASE_KEY, JSON.stringify(this.base));
    } catch { /* storage full or blocked: keep going from memory; the base only improves merges */ }
  }

  /** Take the server's copy of a language: adopt it if we have no unsynced changes, otherwise merge. */
  private receive(id: string, row: RemoteRow) {
    const mine = this.meta.languages[id];
    const local = this.latest.languages[id] as Json | undefined;
    const remote = row.data as Json;
    if (!local || !mine || mine.updatedAt <= mine.syncedAt) {
      this.setLocal(id, remote);
      this.meta.languages[id] = { updatedAt: row.updatedAt, syncedAt: row.updatedAt, serverAt: row.updatedAt };
    } else {
      this.setLocal(id, mergeLanguage(this.base[id], clone(local), clone(remote)));
      // The merged copy is a new local change, still to be pushed on top of the server's version.
      this.meta.languages[id] = { updatedAt: Math.max(Date.now(), mine.updatedAt + 1), syncedAt: mine.syncedAt, serverAt: row.updatedAt };
    }
    this.base[id] = clone(remote);
  }

  private setLocal(id: string, data: Json) {
    const next = (migrateProgress({ version: 2, selectedLanguageId: null, languages: { [id]: data } }) as State).languages[id];
    this.applying = true;
    try {
      if (this.applyToApp) this.applyToApp(id, next);
      else {
        this.latest.languages[id] = next;
        this.storage.setItem(STORAGE_KEY, JSON.stringify(migrateProgress(this.latest)));
      }
      this.snapshot.set(id, JSON.stringify(this.latest.languages[id] ?? next));
    } finally { this.applying = false; }
  }

  private async pushAll(opts: { keepalive?: boolean }) {
    let failed = false;
    for (const id of this.dirty()) {
      try {
        for (let round = 0; round < MAX_MERGE_ROUNDS; round++) {
          const data = this.latest.languages[id];
          const sentAt = this.meta.languages[id]?.updatedAt;
          if (!data || sentAt === undefined) break;
          const sent = clone(data);
          const { ok, row } = await this.api.put(id, sent, this.meta.languages[id]?.serverAt ?? 0, opts);
          if (!ok) { this.receive(id, row); continue; } // another device got there first: merge, then retry
          const now = this.meta.languages[id] ?? { updatedAt: sentAt, syncedAt: 0, serverAt: 0 };
          this.meta.languages[id] = { ...now, syncedAt: sentAt, serverAt: row.updatedAt };
          this.base[id] = sent;
          break;
        }
      } catch { failed = true; }
    }
    this.persistMeta();
    if (failed) this.schedule(RETRY_MS[Math.min(this.failures++, RETRY_MS.length - 1)] ?? 60_000);
    else this.failures = 0;
    this.opts.onOnline?.(!failed);
  }
}
